import React, { createContext, ReactNode, useContext } from "react";
import {
  Flex,
  Grid,
  GridItem,
  GridItemProps,
  GridProps,
  Text,
} from "@chakra-ui/react";

interface GridListTableProps {
  templateColumns: string;
  children: React.ReactNode;
}

export interface GridListTableHeaderItemProps {
  label?: string;
  component?: React.ReactNode;
  align?: "center" | "left" | "right";
}

interface GridListTableHeaderProps extends GridProps {
  headers: GridListTableHeaderItemProps[];
}

interface GridListTableRowProps extends GridProps {
  items: ReactNode[];
  hasAction?: boolean;
  onRowClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  hasScroll?: boolean;
  itemProps?: GridItemProps;
}

interface GridListTableContext {
  templateColumns?: string;
}

const GridListTableContext = createContext<GridListTableContext>({});

function GridListTable({ templateColumns, children }: GridListTableProps) {
  return (
    <GridListTableContext.Provider
      value={{
        templateColumns,
      }}
    >
      <Flex flexDirection={"column"} gap={"12px"}>
        {children}
      </Flex>
    </GridListTableContext.Provider>
  );
}

const Header = ({ headers, ...props }: GridListTableHeaderProps) => {
  const { templateColumns } = useContext(GridListTableContext);

  return (
    <Grid
      templateColumns={templateColumns}
      paddingLeft={"12px"}
      gap={"12px"}
      borderRadius={"8px"}
      boxShadow={"0 0 0 0.5px #8F8F8C, 0 2px 4px 0 rgba(0, 0, 0, 0.10)"}
      height={"36px"}
      alignItems={"center"}
      background={"#FFFFFF"}
      {...props}
    >
      {headers.map((header, index) => (
        <GridItem key={index}>
          {header.component ?? (
            <Text
              textStyle={"body-sm-bold"}
              color={"#444444"}
              textAlign={header.align ?? "center"}
            >
              {header.label}
            </Text>
          )}
        </GridItem>
      ))}
    </Grid>
  );
};

const Row = ({
  items,
  hasAction = true,
  onRowClick,
  hasScroll = false,
  itemProps,
  ...props
}: GridListTableRowProps) => {
  const { templateColumns } = useContext(GridListTableContext);

  return (
    <Grid
      templateColumns={templateColumns}
      paddingLeft={"12px"}
      gap={"12px"}
      borderRadius={"8px"}
      boxShadow={"0 0 0 0.5px #8F8F8C"}
      height={"52px"}
      minHeight={hasScroll ? "52px" : "auto"}
      alignItems={"center"}
      background={"#FFFFFF"}
      {...props}
    >
      {items.map((item, index) => {
        const isEnd = hasAction && index === items.length - 1;
        return (
          <GridItem
            key={index}
            onClick={onRowClick}
            alignSelf={isEnd ? "stretch" : "center"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={isEnd ? "center" : "flex-start"}
            borderRadius={"0 8px 8px 0"}
            boxShadow={
              isEnd
                ? "-0.5px 0 0 0 #8F8F8C, 0 2px 4px 0 rgba(0, 0, 0, 0.10)"
                : "none"
            }
            {...itemProps}
          >
            {item}
          </GridItem>
        );
      })}
    </Grid>
  );
};

GridListTable.Header = Header;
GridListTable.Row = Row;

export default GridListTable;
